/**
 * KullaniciTalepleriPage.jsx — Kullanıcı Ekleme Talepleri, the staff review list (backlog #614; backend #612, company side #613).
 * Route: /Management/Firmalar/KullaniciTalepleri (permission kullanici.olustur: Admin, Kayıt Admin / Moderatör, Saha Admin / Moderatör; root
 * passes). Menu: Firmalar › Kullanıcı Ekleme Talepleri (the item follows the route's permission, #325).
 *
 * A Katılımcı or Dekoratör company asks SAKA for a new login from its portal (Firma Kartı › Users); this page is where staff decide:
 *   · counter boxes per state (Bekliyor / Onaylandı / Reddedildi / Geri çekildi, GET `sayilar`, counted under the other filters) double as the
 *     status filter; pending is the default;
 *   · filters: company (search), fair, free text (person, e-mail, login name, company). All of it lives in the address bar, so Firma Detay can
 *     link here for ONE company (`?firmaId=…&firmaAdi=…`) and a refresh or back keeps the view;
 *   · Onayla opens KullaniciTalebiOnayModal (pre-filled form, one call creates the login), Reddet asks for the reason the company will read.
 * A decided request shows who decided and when, the login it became or the reason it was rejected. Nothing here edits a decided request.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert, Badge, Button, Card, CardBody, Col, Container, Input, InputGroup, Pagination, PaginationItem, PaginationLink, Row, Spinner, Table,
} from "reactstrap";
import { Check, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { SayacKutusu } from "../../../../Components/Common/DurumSayaclari";
import FirmaSearchSelect from "../../../../Components/Firma/FirmaSearchSelect";
import { DURUM_RENK } from "../../../../Components/Firma/KullaniciTalebi";
import { NEUTRAL_GRAY } from "../../../../Components/Projects/StandPills";
import { tarihSaat } from "../../../../Components/Kullanici/KullaniciParcalari";
import { getKullaniciTalepListesi, talepHatasi, TALEP_DURUMU } from "../../../../services/kullaniciTalebiService";
import { fuarService } from "../../../../services/fuarService";
import { KullaniciTalebiOnayModal, KullaniciTalebiRedModal } from "./KullaniciTalebiKararModallari";
import i18n, { currentLocaleTag } from "../../../../i18n";

const SAYFA_BOYU = 25;
const TUMU = "tum";

/** The filter buttons, in the order the company sees the states; `tone` is the counter square's colour (StandPills palette). */
const DURUMLAR = [
  { deger: String(TALEP_DURUMU.BEKLIYOR), sayi: "bekliyor", tone: "warning", etiket: "bekliyor" },
  { deger: String(TALEP_DURUMU.ONAYLANDI), sayi: "onaylandi", tone: "success", etiket: "onaylandi" },
  { deger: String(TALEP_DURUMU.REDDEDILDI), sayi: "reddedildi", tone: "danger", etiket: "reddedildi" },
  { deger: String(TALEP_DURUMU.GERI_CEKILDI), sayi: "geriCekildi", tone: null, etiket: "geriCekildi" },
];

export default function KullaniciTalepleriPage() {
  const { t, i18n: i18nHook } = useTranslation("yonetim");
  const [sp, setSp] = useSearchParams();

  // The whole view comes from the address bar. durum: a state number, "tum" = all states, absent = pending.
  const durumParam = sp.get("durum");
  const durum = durumParam === TUMU ? TUMU : (["0", "1", "2", "3"].includes(durumParam) ? durumParam : String(TALEP_DURUMU.BEKLIYOR));
  const firmaId = Number(sp.get("firmaId")) || null;
  const firmaAdi = sp.get("firmaAdi") || "";
  const fuarId = Number(sp.get("fuarId")) || null;
  const q = sp.get("q") || "";
  const sayfa = Math.max(1, Number(sp.get("sayfa")) || 1);

  const [arama, setArama] = useState(q);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const [fuarlar, setFuarlar] = useState([]);
  const [yenile, setYenile] = useState(0);
  const [onaylanan, setOnaylanan] = useState(null);   // request id
  const [reddedilen, setReddedilen] = useState(null); // request row
  const dil = i18nHook?.language;

  useEffect(() => { document.title = i18n.t("yonetim:kullaniciTalepleri.sayfaBasligi"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const list = await fuarService.getFuarList();
        if (!ignore) setFuarlar(list);
      } catch {
        if (!ignore) setFuarlar([]);   // the filter simply has no fairs; the list itself still works
      }
    }
    run();
    return () => { ignore = true; };
  }, [dil]);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const d = await getKullaniciTalepListesi({
          durum: durum === TUMU ? null : Number(durum), firmaId, fuarId, q: q.trim(), page: sayfa - 1, size: SAYFA_BOYU,
        });
        if (ignore) return;
        const sonSayfa = Math.max(1, Math.ceil((d?.toplam ?? 0) / SAYFA_BOYU));
        if ((d?.toplam ?? 0) > 0 && sayfa > sonSayfa) {
          const next = new URLSearchParams(sp);
          next.set("sayfa", String(sonSayfa));
          setSp(next, { replace: true });
          return;
        }
        setVeri(d);
      } catch (e) {
        if (!ignore) { setHata(talepHatasi(e) || i18n.t("yonetim:kullaniciTalepleri.okunamadi")); setVeri(null); }
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
    // `sp` bilerek bağımlılık dışı: yalnızca sayfa düzeltmesi için okunur, her adres değişiminde yeniden okuma istenmez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durum, firmaId, fuarId, q, sayfa, yenile, dil]);

  function guncelle(patch) {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!("sayfa" in patch)) next.delete("sayfa");
    setSp(next, { replace: true });
  }

  const sayilar = veri?.sayilar || {};
  const talepler = veri?.talepler || [];
  const toplam = veri?.toplam ?? 0;
  const toplamSayfa = Math.max(1, Math.ceil(toplam / SAYFA_BOYU));
  const firmaDegeri = firmaId ? { firmaId, firmaName: firmaAdi || `#${firmaId}` } : null;

  const onaylandi = (cevap) => {
    setOnaylanan(null);
    if (!cevap) return;
    setBilgi(cevap.mesaj);
    setYenile((x) => x + 1);
  };
  const reddedildi = (cevap) => {
    setReddedilen(null);
    if (!cevap) return;
    setBilgi(cevap.mesaj);
    setYenile((x) => x + 1);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("kullaniciTalepleri.baslik")} pageTitle={t("kullaniciTalepleri.firmalar")} />

        <Card className="mb-3">
          <CardBody>
            <div className="d-flex align-items-center flex-wrap gap-2 mb-3" data-testid="kt-durumlar">
              <Button
                color={durum === TUMU ? "primary" : "light"} size="sm" className="d-inline-flex align-items-center gap-2"
                onClick={() => guncelle({ durum: TUMU })} data-testid="kt-durum-tum"
              >
                {t("kullaniciTalepleri.tumu")}
                <SayacKutusu tone={null} value={sayilar.toplam ?? 0} label={t("kullaniciTalepleri.tumu")} />
              </Button>
              {DURUMLAR.map((d) => (
                <Button
                  key={d.deger} color={durum === d.deger ? "primary" : "light"} size="sm" className="d-inline-flex align-items-center gap-2"
                  onClick={() => guncelle({ durum: d.deger === String(TALEP_DURUMU.BEKLIYOR) ? "" : d.deger })} data-testid={`kt-durum-${d.deger}`}
                >
                  {t(`kullaniciTalepleri.durum.${d.etiket}`)}
                  <SayacKutusu tone={d.tone} value={sayilar[d.sayi] ?? 0} label={t(`kullaniciTalepleri.durum.${d.etiket}`)} />
                </Button>
              ))}
            </div>
            <Row className="g-2 align-items-end">
              <Col md={4}>
                <FirmaSearchSelect
                  forceLive label={t("kullaniciTalepleri.firma")} value={firmaDegeri}
                  onChange={(f) => guncelle({ firmaId: f ? String(f.firmaId) : "", firmaAdi: f ? (f.firmaName || "") : "" })}
                />
              </Col>
              <Col md={3}>
                <label className="form-label small mb-1" htmlFor="kt-fuar">{t("kullaniciTalepleri.fuar")}</label>
                <Input id="kt-fuar" type="select" bsSize="sm" value={fuarId ?? ""} onChange={(e) => guncelle({ fuarId: e.target.value })}>
                  <option value="">{t("kullaniciTalepleri.tumFuarlar")}</option>
                  {fuarlar.map((f) => <option key={f.fuarId} value={f.fuarId}>{f.fuarName}</option>)}
                </Input>
              </Col>
              <Col md={5}>
                <label className="form-label small mb-1" htmlFor="kt-ara">{t("kullaniciTalepleri.ara")}</label>
                <InputGroup size="sm">
                  <Input
                    id="kt-ara" value={arama} placeholder={t("kullaniciTalepleri.araIpucu")}
                    onChange={(e) => setArama(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") guncelle({ q: arama.trim() }); }}
                  />
                  <Button color="light" onClick={() => guncelle({ q: arama.trim() })} title={t("kullaniciTalepleri.ara")}><Search size={14} /></Button>
                  {(q || arama) && (
                    <Button color="light" onClick={() => { setArama(""); guncelle({ q: "" }); }} title={t("kullaniciTalepleri.temizle")}><X size={14} /></Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {bilgi && <Alert fade={false} color="success" toggle={() => setBilgi(null)}>{bilgi}</Alert>}
        {hata && <Alert fade={false} color="danger" toggle={() => setHata(null)}>{hata}</Alert>}

        {yukleniyor && !veri && (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        )}

        {veri && (
          <Card style={yukleniyor ? { opacity: 0.5 } : undefined}>
            <CardBody className="p-0">
              {talepler.length === 0 ? (
                <div className="text-center text-muted py-5" data-testid="kt-bos">{t("kullaniciTalepleri.bos")}</div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle mb-0" data-testid="kt-tablo">
                    <thead className="table-light text-muted small text-uppercase">
                      <tr>
                        <th>{t("kullaniciTalepleri.kisi")}</th>
                        <th>{t("kullaniciTalepleri.firma")}</th>
                        <th>{t("kullaniciTalepleri.girisVeRol")}</th>
                        <th>{t("kullaniciTalepleri.talepEden")}</th>
                        <th>{t("kullaniciTalepleri.durumBasligi")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {talepler.map((r) => (
                        <tr key={r.id} data-talep={r.id}>
                          <td>
                            <div className="fw-semibold">{`${r.ad || ""} ${r.soyad || ""}`.trim()}</div>
                            <div className="small text-muted text-break">{r.eposta}</div>
                            {[r.telefon, r.unvan].filter(Boolean).length > 0 && (
                              <div className="small text-muted">{[r.telefon, r.unvan].filter(Boolean).join(" · ")}</div>
                            )}
                            {r.durum === TALEP_DURUMU.BEKLIYOR && Number(r.epostaKullananSayisi) > 0 && (
                              <Badge color="warning" className="fw-normal mt-1" title={t("kullaniciTalepleri.epostaKullanimdaIpucu")} data-testid={`kt-eposta-${r.id}`}>
                                {t("kullaniciTalepleri.epostaKullanimda", { count: Number(r.epostaKullananSayisi) })}
                              </Badge>
                            )}
                          </td>
                          <td className="small">
                            <Link to={`/Management/Firmalar/Detay/${r.firmaId}?tab=portal-kullanicilar${r.fuarId ? `&fuarId=${r.fuarId}` : ""}`} className="fw-semibold">
                              {r.firmaAdi || `#${r.firmaId}`}
                            </Link>
                            {r.fuarAdi && <div className="text-muted">{r.fuarAdi}</div>}
                          </td>
                          <td className="small">
                            <div className="font-monospace">{r.kullaniciAdi}</div>
                            <div className="text-muted">{r.portalRolAdi || "—"}</div>
                          </td>
                          <td className="small">
                            <div>{r.talepEdenAdi || r.talepEdenKullaniciAdi || "—"}</div>
                            <div className="text-muted text-nowrap">{tarihSaat(r.olusturmaTarihi)}</div>
                          </td>
                          <td className="small">
                            <span className="badge text-white fw-normal" style={{ backgroundColor: DURUM_RENK[r.durum] ?? NEUTRAL_GRAY }}>{r.durumAdi}</span>
                            {r.durum !== TALEP_DURUMU.BEKLIYOR && (
                              <div className="text-muted mt-1">
                                {r.kararVerenAdi || ""}{r.kararTarihi ? ` · ${tarihSaat(r.kararTarihi)}` : ""}
                              </div>
                            )}
                            {r.durum === TALEP_DURUMU.ONAYLANDI && r.olusturulanKullaniciAdi && (
                              <div>{t("kullaniciTalepleri.olusanGiris")}: <span className="font-monospace">{r.olusturulanKullaniciAdi}</span></div>
                            )}
                            {r.durum === TALEP_DURUMU.REDDEDILDI && r.redSebebi && (
                              <div className="text-danger">{t("kullaniciTalepleri.redSebebi")}: {r.redSebebi}</div>
                            )}
                          </td>
                          <td className="text-end text-nowrap">
                            {r.durum === TALEP_DURUMU.BEKLIYOR && (
                              <>
                                <Button color="success" size="sm" className="me-1" onClick={() => setOnaylanan(r.id)} data-testid={`kt-onayla-${r.id}`}>
                                  <Check size={14} className="me-1" />{t("kullaniciTalepleri.onayla")}
                                </Button>
                                <Button color="danger" outline size="sm" onClick={() => setReddedilen(r)} data-testid={`kt-reddet-${r.id}`}>
                                  <X size={14} className="me-1" />{t("kullaniciTalepleri.reddet")}
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {toplamSayfa > 1 && (
          <div className="d-flex flex-column align-items-center p-3">
            <Pagination>
              <PaginationItem disabled={sayfa <= 1}>
                <PaginationLink previous onClick={() => guncelle({ sayfa: String(sayfa - 1) })} />
              </PaginationItem>
              <PaginationItem active>
                <PaginationLink>{sayfa}</PaginationLink>
              </PaginationItem>
              <PaginationItem disabled={sayfa >= toplamSayfa}>
                <PaginationLink next onClick={() => guncelle({ sayfa: String(sayfa + 1) })} />
              </PaginationItem>
            </Pagination>
            <div className="text-muted small">
              {t("kullaniciTalepleri.sayfaBilgisi", {
                sayfa, toplamSayfa: toplamSayfa.toLocaleString(currentLocaleTag()), toplam: toplam.toLocaleString(currentLocaleTag()),
              })}
            </div>
          </div>
        )}

        {onaylanan != null && <KullaniciTalebiOnayModal talepId={onaylanan} onClose={onaylandi} />}
        {reddedilen && <KullaniciTalebiRedModal talep={reddedilen} onClose={reddedildi} />}
      </Container>
    </div>
  );
}
